import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { SuspendModal, BlockModal } from '@/components/admin/AccountActionModal';
import { AdminHero, SearchBox, Count } from '@/components/admin/ui';

interface AUser {
  id: string; name: string; email: string; role: string; verified: boolean;
  membershipTier: string; walletGBP: number; listings: number; orders: number;
  suspended: boolean; accountStatus: 'ACTIVE' | 'SUSPENDED' | 'BLOCKED';
  statusReason?: string | null; suspensionReviewDue: boolean; createdAt: string;
}

const COLS = 'grid grid-cols-[2fr_0.7fr_0.9fr_0.7fr_1fr_1.3fr] gap-3';

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '·';
}
function avatarClasses(role: string) {
  if (role === 'ADMIN') return 'bg-ink text-cream';
  if (role === 'SELLER') return 'bg-gold text-ink';
  return 'bg-sage text-cream';
}
function RolePill({ role }: { role: string }) {
  const cls = role === 'ADMIN' ? 'pill-ink' : role === 'SELLER' ? 'pill-gold' : 'pill-sage';
  return <span className={`pill ${cls} text-[9px] tracking-wide`}>{role}</span>;
}

export default function Users() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [modal, setModal] = useState<{ mode: 'suspend' | 'block'; user: AUser } | null>(null);
  const { data: rows = [], isLoading } = useQuery<AUser[]>({
    queryKey: ['admin', 'users', role],
    queryFn: async () => (await api.get('/admin/users', { params: { role: role || undefined } })).data,
  });
  const act = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: string }) => (await api.post(`/admin/users/${id}/${action}`)).data,
    onSuccess: () => { toast.success('Done.'); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Action failed.'),
  });

  const filtered = rows.filter((u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const verified = filtered.filter((u) => u.verified).length;
  const sellers = filtered.filter((u) => u.role === 'SELLER').length;
  const suspended = filtered.filter((u) => u.accountStatus !== 'ACTIVE').length;

  return (
    <div className="p-6 md:p-10 space-y-6">
      <AdminHero
        eyebrow="Admin · Directory"
        title="Users"
        subtitle="Manage buyers and sellers - verify identities, suspend, block or reinstate accounts."
        stats={[
          { label: 'In view', value: filtered.length },
          { label: 'Sellers', value: sellers, accent: 'gold' },
          { label: 'Verified', value: verified, accent: 'ok' },
          { label: 'Suspended', value: suspended, accent: suspended ? 'bad' : undefined },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={q} onChange={setQ} placeholder="Search name or email…" className="w-72" />
        <select value={role} onChange={(e) => setRole(e.target.value)} className="input w-auto text-sm">
          <option value="">All roles</option>
          <option value="BUYER">Buyers</option>
          <option value="SELLER">Sellers</option>
          <option value="ADMIN">Admins</option>
        </select>
        <Count n={filtered.length} noun="user" />
      </div>

      <div className="card overflow-x-auto">
        <div className={`${COLS} px-5 py-3 mono text-mute text-[9px] tracking-[0.14em] uppercase border-b border-warmline bg-cream-deep min-w-[880px]`}>
          <div>User</div><div>Role</div><div>Status</div><div>Wallet</div><div>Activity</div><div className="text-right">Actions</div>
        </div>

        {isLoading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className={`${COLS} px-5 py-3.5 border-b border-warmline last:border-0 items-center min-w-[880px]`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-cream-deep animate-pulse" />
                <div className="space-y-1.5"><div className="h-3 w-32 bg-cream-deep rounded animate-pulse" /><div className="h-2.5 w-40 bg-cream-deep rounded animate-pulse" /></div>
              </div>
              <div className="h-4 w-14 bg-cream-deep rounded-full animate-pulse" />
              <div className="h-4 w-16 bg-cream-deep rounded-full animate-pulse" />
              <div className="h-4 w-12 bg-cream-deep rounded animate-pulse" />
              <div className="h-3 w-24 bg-cream-deep rounded animate-pulse" />
              <div className="h-7 w-24 bg-cream-deep rounded-luxe animate-pulse ml-auto" />
            </div>
          ))
        ) : filtered.map((u) => (
          <div key={u.id} className={`${COLS} px-5 py-3.5 text-sm border-b border-warmline last:border-0 items-center min-w-[880px] transition-colors hover:bg-cream-deep/50 ${u.suspended ? 'opacity-60' : ''}`}>
            <Link to={`/control-7f3a9b2c/users/${u.id}`} className="min-w-0 flex items-center gap-3 group">
              <span className={`shrink-0 w-9 h-9 rounded-full grid place-items-center text-[12px] font-semibold display ${avatarClasses(u.role)}`}>{initials(u.name)}</span>
              <span className="min-w-0">
                <span className="font-medium truncate flex items-center gap-1.5 group-hover:underline decoration-gold-dark underline-offset-2">
                  {u.name}
                  {u.membershipTier === 'MEMBER' && <span className="pill pill-gold text-[8px] py-0.5">★ MEMBER</span>}
                  {u.suspensionReviewDue && <span className="pill pill-warn text-[8px] py-0.5">REVIEW DUE</span>}
                </span>
                <span className="block text-[11px] text-mute truncate">{u.email}</span>
              </span>
            </Link>

            <div><RolePill role={u.role} /></div>

            <div className="flex flex-wrap gap-1.5">
              {u.accountStatus !== 'ACTIVE'
                ? <span className="pill pill-bad text-[9px]">{u.accountStatus}</span>
                : <span className="pill pill-ok text-[9px]">ACTIVE</span>}
              {u.verified
                ? <span className="pill pill-sage text-[9px]">✓ ID</span>
                : <span className="pill bg-cream-deep text-mute text-[9px]">No ID</span>}
            </div>

            <div className="display text-base">£{u.walletGBP.toFixed(2)}</div>

            <div className="text-[11px] text-mute leading-tight">
              <div><span className="text-ink font-medium">{u.listings}</span> listings</div>
              <div><span className="text-ink font-medium">{u.orders}</span> orders</div>
            </div>

            <div className="flex justify-end gap-1.5 flex-wrap">
              {!u.verified && <button onClick={() => act.mutate({ id: u.id, action: 'verify' })} className="btn btn-sm btn-outline text-[11px] py-1 px-2.5">Verify</button>}
              {u.accountStatus !== 'ACTIVE' ? (
                <>
                  <button onClick={() => act.mutate({ id: u.id, action: 'reinstate' })} className="btn btn-sm btn-gold !text-ink text-[11px] py-1 px-2.5">Reinstate</button>
                  {u.accountStatus === 'SUSPENDED' && (
                    <button onClick={() => setModal({ mode: 'block', user: u })} className="btn btn-sm text-[11px] py-1 px-2.5 bg-ink text-cream hover:opacity-90">Escalate to Block</button>
                  )}
                </>
              ) : u.role !== 'ADMIN' ? (
                <>
                  <button onClick={() => setModal({ mode: 'suspend', user: u })} className="btn btn-sm text-[11px] py-1 px-2.5 bg-bad text-cream hover:opacity-90">Suspend</button>
                  <button onClick={() => setModal({ mode: 'block', user: u })} className="btn btn-sm btn-outline text-[11px] py-1 px-2.5">Block</button>
                </>
              ) : (
                <span className="text-[11px] text-mute-soft italic self-center">Protected</span>
              )}
            </div>
          </div>
        ))}

        {!isLoading && !filtered.length && (
          <div className="px-5 py-16 text-center">
            <div className="text-3xl mb-2 opacity-60">👤</div>
            <div className="display text-lg">No users found</div>
            <p className="text-mute text-sm mt-1">{q ? `Nothing matches “${q}”.` : 'Try a different role filter.'}</p>
          </div>
        )}
      </div>

      {modal?.mode === 'suspend' && <SuspendModal userId={modal.user.id} userName={modal.user.name} onClose={() => setModal(null)} />}
      {modal?.mode === 'block' && <BlockModal userId={modal.user.id} userName={modal.user.name} onClose={() => setModal(null)} />}
    </div>
  );
}
